import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { motion } from 'framer-motion';

interface GPUUtilizationChartProps {
  history: Array<{
    timestamp: string;
    gpu: {
      utilization: number;
    };
  }>;
  currentUtilization: number;
}

const GPUUtilizationChart: React.FC<GPUUtilizationChartProps> = ({ history, currentUtilization }) => {
  // Prepare data for chart
  const chartData = history.map((item, index) => ({
    time: index,
    utilization: item.gpu.utilization
  }));

  // Add empty data points if we don't have 60 seconds of history yet
  while (chartData.length < 60) {
    chartData.unshift({
      time: chartData.length * -1,
      utilization: 0
    });
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 px-3 py-2 rounded shadow-lg border border-gray-700">
          <p className="text-sm font-medium text-orange-400">
            {`${payload[0].value.toFixed(1)}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-800">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">GPU Utilization</h3>
        <motion.span 
          key={currentUtilization}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`text-lg font-bold ${
            currentUtilization > 80 ? 'text-red-400' : 
            currentUtilization > 50 ? 'text-orange-400' : 
            'text-green-400'
          }`}
        >
          {currentUtilization.toFixed(1)}%
        </motion.span>
      </div>
      
      <ResponsiveContainer width="100%" height={80}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <defs>
            <linearGradient id="gpuGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.6}/>
              <stop offset="95%" stopColor="#60a5fa" stopOpacity={0.05}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
          <XAxis 
            dataKey="time" 
            hide={true}
          />
          <YAxis 
            domain={[0, 100]}
            hide={true}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="utilization"
            stroke="#60a5fa"
            strokeWidth={1.5}
            fill="url(#gpuGradient)"
            animationDuration={300}
          />
        </AreaChart>
      </ResponsiveContainer>
      
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>60s ago</span>
        <span>30s</span>
        <span>now</span>
      </div>
    </div>
  );
};

export default GPUUtilizationChart;